import { ElementDefinition } from 'cytoscape';
import { InterfaceVlanInfo, TopologyConnection, TopologyData, TopologyDevice, TopologyEndpoint, TopologyVlan } from '../hooks/useTopology';
import { LinkSpeed, VlanData } from '../types/layers';
import type { IconTheme } from './iconThemes';

/** Per-device cap on rendered endpoint satellite nodes — past this, the
 * remainder collapse into a single "+N more" overflow node so a busy
 * 48-port access switch doesn't turn into an unreadable starburst. */
const MAX_ENDPOINTS_PER_DEVICE = 20;

/**
 * Color palette for VLAN visualization
 * Uses distinct colors to help identify different VLANs
 */
const VLAN_COLORS = [
  '#3273dc', // Blue
  '#48c774', // Green
  '#f14668', // Red
  '#ffdd57', // Yellow
  '#00d1b2', // Cyan
  '#b86bff', // Purple
  '#ff8c42', // Orange
  '#ff6b9d', // Pink
  '#20c997', // Teal
  '#6610f2', // Indigo
];

/**
 * Get a consistent color for a VLAN ID
 * Uses modulo to cycle through colors for large VLAN IDs
 */
function getVlanColor(vlanId: number): string {
  return VLAN_COLORS[vlanId % VLAN_COLORS.length];
}

/**
 * Transform device VLANs to the VlanData format expected by layer renderers
 */
function transformDeviceVlans(vlans: TopologyVlan[] | undefined): VlanData[] | undefined {
  if (!vlans || vlans.length === 0) return undefined;

  return vlans.map((vlan) => ({
    vlanId: vlan.vlanId,
    name: vlan.vlanName || `VLAN ${String(vlan.vlanId)}`,
    color: getVlanColor(vlan.vlanId),
  }));
}

/**
 * Convert speed in bps to a LinkSpeed category
 */
export function speedBpsToCategory(speedBps: number | undefined): LinkSpeed {
  if (speedBps === undefined) return 'unknown';

  // 10 Gbps = 10,000,000,000 bps
  if (speedBps >= 10_000_000_000) return '10g';
  // 1 Gbps = 1,000,000,000 bps
  if (speedBps >= 1_000_000_000) return '1g';
  // 100 Mbps = 100,000,000 bps
  if (speedBps >= 100_000_000) return '100m';
  // 10 Mbps = 10,000,000 bps (often seen on sleeping/power-saving devices)
  if (speedBps >= 10_000_000) return '10m';

  return 'unknown';
}

/**
 * Options for topology transformation
 */
export interface TransformOptions {
  /** Icon theme for node icons (optional) */
  iconTheme?: IconTheme;
  /** End-user devices to render as satellite nodes around their attachment
   * device (endpoints overlay). Omit or pass `[]` to render none. */
  endpoints?: TopologyEndpoint[];
}

/**
 * Build the satellite nodes/edges for the endpoints overlay: one node+edge
 * per endpoint (capped per device), plus an aggregate "+N more" node+edge
 * for whatever doesn't fit under the cap. Endpoints whose attachment points
 * at a device that isn't in this topology are skipped defensively — that
 * can legitimately happen for a region-scoped topology fetch if an endpoint
 * moved to a device in another region between the two queries.
 */
function buildEndpointElements(
  endpoints: TopologyEndpoint[],
  deviceIds: Set<string>
): ElementDefinition[] {
  const elements: ElementDefinition[] = [];

  const byDevice = new Map<string, TopologyEndpoint[]>();
  endpoints.forEach((endpoint) => {
    const deviceId = endpoint.attachment.deviceId;
    if (!deviceIds.has(deviceId)) return;
    let group = byDevice.get(deviceId);
    if (!group) {
      group = [];
      byDevice.set(deviceId, group);
    }
    group.push(endpoint);
  });

  byDevice.forEach((deviceEndpoints, deviceId) => {
    // Sort by id so which endpoints are "visible" vs. "overflow" is stable
    // across re-renders/re-fetches rather than depending on array order.
    const sorted = deviceEndpoints.slice().sort((a, b) => a.id.localeCompare(b.id));
    const visible = sorted.slice(0, MAX_ENDPOINTS_PER_DEVICE);
    const overflowCount = sorted.length - visible.length;

    visible.forEach((endpoint) => {
      const label = endpoint.hostname ?? endpoint.ouiVendor ?? endpoint.macAddress;
      const nodeId = `endpoint:${endpoint.id}`;

      elements.push({
        data: {
          id: nodeId,
          label,
          isEndpoint: true,
          parentDeviceId: deviceId,
          endpointKind: endpoint.attachment.kind,
          macAddress: endpoint.macAddress,
          classification: endpoint.classification,
        },
        // Grabbable: users can drag satellites into a custom arrangement.
        // The spot isn't persisted — the next parent move or layout run
        // re-fans them into the ring (deliberate simplicity).
      });

      const edgeData: Record<string, unknown> = {
        id: `endpoint-edge:${endpoint.id}`,
        source: nodeId,
        target: deviceId,
        endpointLink: endpoint.attachment.kind,
        endpointId: endpoint.id,
        endpointLabel: label,
      };
      if (endpoint.attachment.linkId !== undefined) edgeData.linkId = endpoint.attachment.linkId;
      if (endpoint.attachment.interfaceName !== undefined) edgeData.interfaceName = endpoint.attachment.interfaceName;
      // Only set the fields that actually exist on this attachment — the
      // wired/wireless halves are mutually-exclusive-ish and the backend
      // omits whichever doesn't apply.
      if (endpoint.attachment.ssid !== undefined) edgeData.ssid = endpoint.attachment.ssid;
      if (endpoint.attachment.band !== undefined) edgeData.band = endpoint.attachment.band;
      if (endpoint.attachment.channel !== undefined) edgeData.channel = endpoint.attachment.channel;
      if (endpoint.attachment.signalDbm !== undefined) edgeData.signalDbm = endpoint.attachment.signalDbm;
      if (endpoint.attachment.vlanId !== undefined) edgeData.vlanId = endpoint.attachment.vlanId;

      elements.push({ data: edgeData });
    });

    if (overflowCount > 0) {
      const overflowNodeId = `endpoint-overflow:${deviceId}`;

      elements.push({
        data: {
          id: overflowNodeId,
          isEndpointOverflow: true,
          parentDeviceId: deviceId,
          count: overflowCount,
          label: `+${String(overflowCount)} more`,
        },
      });

      elements.push({
        data: {
          id: `endpoint-overflow-edge:${deviceId}`,
          source: overflowNodeId,
          target: deviceId,
          endpointLink: 'aggregate',
        },
      });
    }
  });

  return elements;
}

/**
 * Transform topology data from the API into Cytoscape elements
 *
 * @param topology - The topology data from the API containing devices and connections
 * @param options - Optional configuration including icon theme
 * @returns Array of Cytoscape element definitions (nodes and edges)
 */
export function transformTopologyToElements(
  topology: TopologyData,
  options: TransformOptions = {}
): ElementDefinition[] {
  const elements: ElementDefinition[] = [];
  const { iconTheme, endpoints } = options;

  // Transform devices into nodes
  topology.devices.forEach((device: TopologyDevice) => {
    // Get icon URL from theme if provided
    const iconUrl = iconTheme?.getIconUrl({
      id: device.id,
      label: device.name || device.ipAddress,
      deviceType: device.deviceType,
      ipAddress: device.ipAddress,
      status: device.status,
      isMonitored: device.isMonitored,
    }) || '';

    elements.push({
      data: {
        id: device.id,
        label: device.name || device.ipAddress,
        // Store additional device data for tooltip/selection display
        deviceType: device.deviceType,
        ipAddress: device.ipAddress,
        status: device.status,
        regionId: device.regionId,
        divisionId: device.divisionId,
        isMonitored: device.isMonitored,
        // Icon for node visualization
        iconUrl,
        // VLAN layer data
        vlans: transformDeviceVlans(device.vlans),
      },
    });
  });

  // Transform connections into edges
  // We need to handle three cases:
  // 1. Bidirectional dedup: A→B and B→A with same interfaces = 1 edge
  // 2. Multiple physical links: Different interface pairs = separate edges
  // 3. LAG aggregation: Multiple links in same LAG = 1 logical edge with member count

  // Step 1: Normalize and deduplicate connections
  // Use relaxed matching: merge if at least one interface name matches
  // Keep the connection with more complete interface data

  interface NormalizedConnection {
    device1Id: string;
    device1Iface: string | undefined;
    device2Id: string;
    device2Iface: string | undefined;
    original: TopologyConnection;
  }

  // Normalize all connections (smaller device ID first)
  const normalizedConnections: NormalizedConnection[] = topology.connections.map((conn) => {
    const sourceId = conn.sourceDeviceId;
    const targetId = conn.targetDeviceId;

    if (sourceId <= targetId) {
      return {
        device1Id: sourceId,
        device1Iface: conn.source.interfaceName,
        device2Id: targetId,
        device2Iface: conn.target.interfaceName,
        original: conn,
      };
    } else {
      return {
        device1Id: targetId,
        device1Iface: conn.target.interfaceName,
        device2Id: sourceId,
        device2Iface: conn.source.interfaceName,
        original: conn,
      };
    }
  });

  // Group by device pair first
  const devicePairMap = new Map<string, NormalizedConnection[]>();
  normalizedConnections.forEach((norm) => {
    const pairKey = `${norm.device1Id}-${norm.device2Id}`;
    let group = devicePairMap.get(pairKey);
    if (!group) {
      group = [];
      devicePairMap.set(pairKey, group);
    }
    group.push(norm);
  });

  // For each device pair, merge connections that share at least one known interface
  const processedConnections = new Map<string, TopologyConnection>();

  devicePairMap.forEach((connections, pairKey) => {
    // Track which connections have been merged
    const merged = new Set<number>();

    for (let i = 0; i < connections.length; i++) {
      if (merged.has(i)) continue;

      let best = connections[i];
      const mergedConnections: NormalizedConnection[] = [best];
      merged.add(i);

      // Look for other connections that should merge with this one
      for (let j = i + 1; j < connections.length; j++) {
        if (merged.has(j)) continue;

        const other = connections[j];

        // Check if they share at least one known interface on the same side
        const iface1Matches = best.device1Iface && other.device1Iface && best.device1Iface === other.device1Iface;
        const iface2Matches = best.device2Iface && other.device2Iface && best.device2Iface === other.device2Iface;

        // Also match if one has a known interface and the other is unknown on that side
        const iface1Compatible = best.device1Iface === other.device1Iface || !best.device1Iface || !other.device1Iface;
        const iface2Compatible = best.device2Iface === other.device2Iface || !best.device2Iface || !other.device2Iface;

        // Merge if: at least one side matches exactly, OR both sides are compatible (one known, one unknown)
        const shouldMerge = iface1Matches || iface2Matches || (iface1Compatible && iface2Compatible);

        if (shouldMerge) {
          merged.add(j);
          mergedConnections.push(other);

          // Keep the one with more complete interface data
          const bestScore = (best.device1Iface ? 1 : 0) + (best.device2Iface ? 1 : 0);
          const otherScore = (other.device1Iface ? 1 : 0) + (other.device2Iface ? 1 : 0);

          if (otherScore > bestScore) {
            best = other;
          }
        }
      }

      // Build merged connection with combined endpoint data from all merged connections
      // This ensures VLANs from bidirectional links are preserved
      const mergedConnection = { ...best.original };

      // Combine VLANs from all merged connections
      // Since normalization might swap source/target, we need to match by device ID
      const sourceVlans = new Map<number, InterfaceVlanInfo>();
      const targetVlans = new Map<number, InterfaceVlanInfo>();

      mergedConnections.forEach((norm) => {
        const orig = norm.original;
        // Determine which endpoint in the original corresponds to source/target in the merged result
        const origSourceIsDevice1 = orig.sourceDeviceId === best.device1Id;

        if (origSourceIsDevice1) {
          // Original source -> merged source, original target -> merged target
          orig.source.vlans?.forEach((v) => sourceVlans.set(v.vlanId, v));
          orig.target.vlans?.forEach((v) => targetVlans.set(v.vlanId, v));
        } else {
          // Original target -> merged source, original source -> merged target
          orig.target.vlans?.forEach((v) => sourceVlans.set(v.vlanId, v));
          orig.source.vlans?.forEach((v) => targetVlans.set(v.vlanId, v));
        }
      });

      // Apply merged VLANs to the connection endpoints
      // The mergedConnection keeps the original orientation of best.original
      // We need to map device1/device2 VLANs to the correct source/target based on orientation
      const bestSourceIsDevice1 = best.original.sourceDeviceId === best.device1Id;

      // Determine which VLANs go to which endpoint based on the original's orientation
      const vlansForSource = bestSourceIsDevice1 ? sourceVlans : targetVlans;
      const vlansForTarget = bestSourceIsDevice1 ? targetVlans : sourceVlans;

      if (vlansForSource.size > 0) {
        mergedConnection.source = {
          ...mergedConnection.source,
          vlans: Array.from(vlansForSource.values()),
        };
      }
      if (vlansForTarget.size > 0) {
        mergedConnection.target = {
          ...mergedConnection.target,
          vlans: Array.from(vlansForTarget.values()),
        };
      }

      // Create a unique key for this deduplicated connection
      const connKey = `${pairKey}:${best.device1Iface ?? 'unknown'}-${best.device2Iface ?? 'unknown'}`;
      processedConnections.set(connKey, mergedConnection);
    }
  });

  // Step 2: Group connections by LAG membership for aggregation
  // If both endpoints are LAG members of the same LAG, aggregate them
  interface AggregatedLink {
    lagKey: string;
    connections: TopologyConnection[];
    sourceLagId: string;
    sourceLagName: string;
    targetLagId: string;
    targetLagName: string;
  }

  const lagGroups = new Map<string, AggregatedLink>();
  const standaloneConnections: TopologyConnection[] = [];

  processedConnections.forEach((connection) => {
    const sourceLag = connection.source.lagMembership;
    const targetLag = connection.target.lagMembership;

    // Check if both ends are LAG members - aggregate them
    if (sourceLag && targetLag) {
      // Create LAG-to-LAG key (normalized)
      const [lag1, lag2] = [
        { deviceId: connection.sourceDeviceId, lagId: sourceLag.interfaceId, lagName: sourceLag.interfaceName },
        { deviceId: connection.targetDeviceId, lagId: targetLag.interfaceId, lagName: targetLag.interfaceName },
      ].sort((a, b) => `${a.deviceId}:${a.lagId}`.localeCompare(`${b.deviceId}:${b.lagId}`));

      const lagKey = `lag:${lag1.deviceId}:${lag1.lagId}-${lag2.deviceId}:${lag2.lagId}`;

      let group = lagGroups.get(lagKey);
      if (!group) {
        group = {
          lagKey,
          connections: [],
          sourceLagId: lag1.lagId,
          sourceLagName: lag1.lagName,
          targetLagId: lag2.lagId,
          targetLagName: lag2.lagName,
        };
        lagGroups.set(lagKey, group);
      }
      group.connections.push(connection);
    } else {
      standaloneConnections.push(connection);
    }
  });

  // Step 3: Group standalone connections by device pair for multi-link rendering
  const devicePairGroups = new Map<string, TopologyConnection[]>();
  standaloneConnections.forEach((connection) => {
    const [id1, id2] = [connection.sourceDeviceId, connection.targetDeviceId].sort();
    const pairKey = `${id1}-${id2}`;

    let pairGroup = devicePairGroups.get(pairKey);
    if (!pairGroup) {
      pairGroup = [];
      devicePairGroups.set(pairKey, pairGroup);
    }
    pairGroup.push(connection);
  });

  // Step 4: Create edges for LAG aggregated connections
  lagGroups.forEach((group) => {
    // Use the first connection as the base, add aggregation info
    const baseConn = group.connections[0];
    const totalSpeed = group.connections.reduce((sum, c) => sum + (c.speedBps || 0), 0);
    const speedCategory = speedBpsToCategory(totalSpeed);

    // Collect VLANs from all member connections AND from the LAG interface itself
    // VLANs are typically configured on the LAG interface, not member ports
    const vlanIds = new Set<number>();
    const sourceVlans = new Map<number, InterfaceVlanInfo>();
    const targetVlans = new Map<number, InterfaceVlanInfo>();

    group.connections.forEach((conn) => {
      // Collect from member port VLANs (less common)
      conn.source.vlans?.forEach((v) => {
        vlanIds.add(v.vlanId);
        sourceVlans.set(v.vlanId, v);
      });
      conn.target.vlans?.forEach((v) => {
        vlanIds.add(v.vlanId);
        targetVlans.set(v.vlanId, v);
      });

      // Collect from LAG interface VLANs (more common - this is where VLANs typically live)
      conn.source.lagMembership?.vlans?.forEach((v) => {
        vlanIds.add(v.vlanId);
        sourceVlans.set(v.vlanId, v);
      });
      conn.target.lagMembership?.vlans?.forEach((v) => {
        vlanIds.add(v.vlanId);
        targetVlans.set(v.vlanId, v);
      });
    });

    elements.push({
      data: {
        id: `lag-${group.lagKey}`,
        source: baseConn.sourceDeviceId,
        target: baseConn.targetDeviceId,
        connectionType: 'lag',
        confidenceScore: Math.max(...group.connections.map((c) => c.confidenceScore)),
        physical: {
          speed: speedCategory,
          speedBps: totalSpeed,
        },
        vlans: vlanIds.size > 0 ? Array.from(vlanIds).sort((a, b) => a - b) : undefined,
        // LAG-specific data
        isAggregated: true,
        physicalLinkCount: group.connections.length,
        sourceLagName: group.sourceLagName,
        targetLagName: group.targetLagName,
        // Store member link details for drill-down
        memberLinks: group.connections.map((c) => ({
          sourceInterface: c.source.interfaceName,
          targetInterface: c.target.interfaceName,
          speedBps: c.speedBps,
        })),
        // Use LAG endpoints for display, with collected VLANs
        sourceEndpoint: {
          ...baseConn.source,
          interfaceId: group.sourceLagId,
          interfaceName: group.sourceLagName,
          vlans: sourceVlans.size > 0 ? Array.from(sourceVlans.values()) : undefined,
        },
        targetEndpoint: {
          ...baseConn.target,
          interfaceId: group.targetLagId,
          interfaceName: group.targetLagName,
          vlans: targetVlans.size > 0 ? Array.from(targetVlans.values()) : undefined,
        },
      },
    });
  });

  // Step 5: Create edges for standalone connections (with multi-link index)
  devicePairGroups.forEach((connections) => {
    const linkCount = connections.length;

    connections.forEach((connection, index) => {
      const speedCategory = speedBpsToCategory(connection.speedBps);

      // Collect VLAN IDs from both endpoints
      const vlanIds = new Set<number>();
      connection.source.vlans?.forEach((v) => vlanIds.add(v.vlanId));
      connection.target.vlans?.forEach((v) => vlanIds.add(v.vlanId));

      elements.push({
        data: {
          id: connection.id,
          source: connection.sourceDeviceId,
          target: connection.targetDeviceId,
          connectionType: connection.connectionType,
          confidenceScore: connection.confidenceScore,
          physical: {
            speed: speedCategory,
            speedBps: connection.speedBps,
          },
          vlans: vlanIds.size > 0 ? Array.from(vlanIds).sort((a, b) => a - b) : undefined,
          sourceEndpoint: connection.source,
          targetEndpoint: connection.target,
          // Multi-link metadata for rendering
          isAggregated: false,
          multiLinkIndex: index,
          multiLinkTotal: linkCount,
        },
      });
    });
  });

  // Endpoints overlay: satellite nodes/edges for end-user devices, added
  // last so their edges never interfere with the connection-merging logic
  // above (which only ever looks at `topology.connections`).
  if (endpoints && endpoints.length > 0) {
    const deviceIds = new Set(topology.devices.map((d) => d.id));
    elements.push(...buildEndpointElements(endpoints, deviceIds));
  }

  return elements;
}

/**
 * Get device type color for visualization
 *
 * @param deviceType - The type of device (router, switch, etc.)
 * @returns A color hex code for the device type
 */
export function getDeviceTypeColor(deviceType: string): string {
  const colorMap: Record<string, string> = {
    'router': '#3273dc',    // Bulma primary blue
    'switch': '#48c774',    // Bulma success green
    'server': '#f14668',    // Bulma danger red
    'firewall': '#ffdd57',  // Bulma warning yellow
    'access-point': '#00d1b2', // Bulma info cyan
  };

  return colorMap[deviceType.toLowerCase()] || '#7a7a7a'; // Default gray
}

/**
 * Get status color for device visualization
 *
 * @param status - The status of the device (online, offline, etc.)
 * @returns A color hex code for the status
 */
export function getStatusColor(status: string): string {
  const colorMap: Record<string, string> = {
    'online': '#48c774',   // Green
    'offline': '#f14668',  // Red
    'warning': '#ffdd57',  // Yellow
    'unknown': '#7a7a7a',  // Gray
  };

  return colorMap[status.toLowerCase()] || '#7a7a7a';
}
